/* 類別管理：抽屜的形狀決定了你每次建檔時的歸位，
   所以它必須能在 App 裡改 —— 而不是被種子清單寫死。

   兩個原則：
   1. 改名、新增、刪除都走 op 管線，所以會同步到其他裝置（刪除是軟刪除）。
   2. 刪掉一個抽屜不等於丟掉裡面的知識：那些知識退回「未分類」，
      而且退回這件事本身也是一筆要同步出去的改動。 */
import { useState } from "react";
import type { Category, Concept } from "../domain/types.ts";
import { UNCATEGORIZED_ID } from "../domain/seed.ts";
import { useI18n } from "../i18n/index.tsx";
import { useCategoryName } from "./views.tsx";

const REVIEW_OPTIONS = [30, 90, 180, 365, 730, 1825];
const CAP_OPTIONS = [6, 12, 20, 40];

export interface CategoryDraft {
  id?: string;
  parentId: string | null;
  name: string;
  definition: string;
  reviewDefaultDays: number;
  linkCapPerNode: number;
}

function draftOf(c: Category, displayName: string): CategoryDraft {
  return {
    id: c.id, parentId: c.parentId, name: displayName, definition: c.definition,
    reviewDefaultDays: c.reviewDefaultDays, linkCapPerNode: c.linkCapPerNode,
  };
}

export function CategoryPanel(props: {
  categories: Category[]; concepts: Concept[];
  onSave: (draft: CategoryDraft) => Promise<void>;
  onDelete: (category: Category) => Promise<void>;
  onClose: () => void;
}) {
  const { t, tn } = useI18n();
  const nameOf = useCategoryName();
  const [draft, setDraft] = useState<CategoryDraft | null>(null);
  const [confirmId, setConfirmId] = useState("");
  const [busy, setBusy] = useState(false);

  const mains = props.categories.filter((c) => !c.parentId);
  const subsOf = (id: string) => props.categories.filter((c) => c.parentId === id);
  /* 抽屜裡有幾則：主類別看歸位，副類別看標記 —— 同一則知識可以在多個抽屜裡被找到。 */
  const countOf = (id: string) => props.concepts.filter(
    (c) => c.primaryCategoryId === id || c.subCategoryIds.includes(id)).length;

  const save = async () => {
    if (!draft || !draft.name.trim() || busy) return;
    setBusy(true);
    try { await props.onSave({ ...draft, name: draft.name.trim() }); setDraft(null); }
    finally { setBusy(false); }
  };

  const remove = async (category: Category) => {
    if (busy) return;
    setBusy(true);
    try { await props.onDelete(category); setConfirmId(""); setDraft(null); }
    finally { setBusy(false); }
  };

  const editor = (
    <div className="catcard" data-cat-editor>
      <input className="input" aria-label={t("cat.name")} placeholder={t("cat.name")}
        data-cat-name value={draft?.name ?? ""}
        onChange={(e) => draft && setDraft({ ...draft, name: e.target.value })} />
      <textarea aria-label={t("cat.definition")} placeholder={t("cat.definition.placeholder")}
        style={{ marginTop: 8, minHeight: 64 }} data-cat-definition
        value={draft?.definition ?? ""}
        onChange={(e) => draft && setDraft({ ...draft, definition: e.target.value })} />
      <div className="row" style={{ marginTop: 8 }}>
        <select className="input" aria-label={t("cat.reviewDefaultDays")}
          value={String(draft?.reviewDefaultDays ?? 365)}
          onChange={(e) => draft && setDraft({ ...draft, reviewDefaultDays: Number(e.target.value) })}>
          {REVIEW_OPTIONS.map((d) => <option key={d} value={d}>{tn("field.days", d)}</option>)}
        </select>
        <select className="input" aria-label={t("cat.linkCap")}
          value={String(draft?.linkCapPerNode ?? 12)}
          onChange={(e) => draft && setDraft({ ...draft, linkCapPerNode: Number(e.target.value) })}>
          {CAP_OPTIONS.map((n) => <option key={n} value={n}>{t("cat.linkCap.value", { n })}</option>)}
        </select>
      </div>
      <div className="row" style={{ marginTop: 12 }}>
        <button className="btn primary" disabled={!draft?.name.trim() || busy} data-cat-save
          onClick={() => void save()}>{t("edit.save")}</button>
        <button className="btn" onClick={() => setDraft(null)}>{t("edit.cancel")}</button>
      </div>
    </div>
  );

  const row = (c: Category, isSub: boolean) => (
    <div className={"catrow" + (isSub ? " sub" : "")} key={c.id} data-cat-row={c.id}>
      <div className="row between" style={{ alignItems: "baseline" }}>
        <span>
          <span className={isSub ? "" : "dn"}>{nameOf(c)}</span>
          {c.system && <span className="mono dim"> · {t("cat.system")}</span>}
          <span className="mono dim"> · {tn("cabinet.count", countOf(c.id))}</span>
        </span>
        <span className="row" style={{ gap: 8 }}>
          {/* 系統保留的抽屜連改名都不給：它同時是「還沒歸位」的收容所，
              名字被改掉之後，就沒有人認得出它是什麼了。 */}
          {!c.system && <button className="btn small" data-cat-edit={c.id}
            onClick={() => { setDraft(draftOf(c, nameOf(c))); setConfirmId(""); }}>{t("cat.edit")}</button>}
          {!c.system && (confirmId === c.id
            ? <>
                <button className="btn small danger" disabled={busy} data-cat-delete-confirm={c.id}
                  onClick={() => void remove(c)}>{t("cat.delete.confirm")}</button>
                <button className="btn small" onClick={() => setConfirmId("")}>{t("edit.cancel")}</button>
              </>
            : <button className="btn small" data-cat-delete={c.id}
                onClick={() => { setConfirmId(c.id); setDraft(null); }}>{t("cat.delete")}</button>)}
        </span>
      </div>
      {confirmId === c.id && <p className="hint bad" style={{ margin: "8px 0 0" }}>{t("cat.delete.hint")}</p>}
      {draft && draft.id === c.id && editor}
    </div>
  );

  return (
    <section className="sheetpanel" data-cat-panel>
      <div className="row between">
        <h2>{t("cat.title")}</h2>
        <button className="iconbtn" onClick={props.onClose} aria-label={t("settings.title")}>✕</button>
      </div>
      <p className="hint">{t("cat.hint")}</p>

      {mains.map((main) => (
        <div key={main.id} style={{ marginTop: 12 }}>
          {row(main, false)}
          {subsOf(main.id).map((sub) => row(sub, true))}
          <button className="btn small" style={{ marginTop: 8 }} data-cat-new-sub={main.id}
            onClick={() => { setConfirmId(""); setDraft({
              parentId: main.id, name: "", definition: "", reviewDefaultDays: main.reviewDefaultDays,
              linkCapPerNode: main.linkCapPerNode }); }}>{t("cat.new.sub")}</button>
          {draft && !draft.id && draft.parentId === main.id && editor}
        </div>
      ))}

      <div className="row" style={{ marginTop: 20 }}>
        <button className="btn primary" data-cat-new-main disabled={!!(draft && !draft.id)}
          onClick={() => { setConfirmId(""); setDraft({
            parentId: null, name: "", definition: "", reviewDefaultDays: 365,
            linkCapPerNode: 12 }); }}>{t("cat.new.main")}</button>
      </div>
      {draft && !draft.id && draft.parentId === null && editor}

      <p className="hint dim" style={{ marginTop: 12 }} data-cat-uncategorized-hint>
        {t("cat.uncategorized", { name: nameOf(props.categories.find((c) => c.id === UNCATEGORIZED_ID)) })}
      </p>
    </section>
  );
}
